/* eslint-disable max-len */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

import { mdms } from '../../../api';
import Icon from '../../OC/oc-icon';
import { connectLocation } from '../../location/LocationConnectors';

const EmailInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 12px 45px 12px 12px;
  border: 2px solid
    ${props => {
      if (props.hasError) return '#940420';
      if (props.status === 'checking') return '#ffa500';
      if (props.status === 'exists') return '#940420';
      if (props.status === 'available') return '#28a745';
      return '#ccc';
    }};
  border-radius: 4px;
  font-size: 14px;
  font-family: OCRoboto, Arial, sans-serif;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => {
      if (props.hasError) return '#940420';
      if (props.status === 'checking') return '#ffa500';
      if (props.status === 'exists') return '#940420';
      if (props.status === 'available') return '#28a745';
      return '#d40f7d';
    }};
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const StatusIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  color: ${props => {
    if (props.status === 'checking') return '#ffa500';
    if (props.status === 'exists') return '#940420';
    if (props.status === 'available') return '#28a745';
    return '#6d6d6d';
  }};
`;

const StatusMessage = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-family: OCRoboto, Arial, sans-serif;
  color: ${props => {
    if (props.type === 'error') return '#940420';
    if (props.type === 'success') return '#28a745';
    if (props.type === 'checking') return '#d1d1d1';
    return '#6d6d6d';
  }};
  font-weight: ${props => (props.type === 'error' ? 'bold' : 'normal')};
`;

const AlertBox = styled.div`
  margin-top: 16px;
  padding: 16px 20px;
  background: #F7DBDB;
  color: #660014;
  font-family: OCRoboto, Arial, sans-serif;
  font-size: 14px;
  line-height: 1.4;

  strong {
    font-weight: 700;
    color: #660014;
  }

  a {
    color: ;#660014;
    text-decoration: underline;
    font-weight: 600;

    &:hover {
      text-decoration: none;
    }
  }
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #ffa500;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const isValidEmail = email => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

const EmailCheckWidget = props => {
  const {
    id,
    value = '',
    disabled = false,
    readonly = false,
    placeholder,
    onChange,
    options = {},
    rawErrors = [],
    formContext = {},
    t,
  } = props;

  const {
    checkApiUrl = '/api/v1/contractor/users/contractor_rewards_lookup',
    debounceMs = 500,
    showAvailableMessage = true,
    showExistsMessage = true,
    customMessages = {},
  } = options;

  const [status, setStatus] = useState('idle'); // idle, checking, exists, available, error, under_review
  const [statusMessage, setStatusMessage] = useState('');
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Function to update validation status in form context
  const updateEmailValidationStatus = useCallback(
    (emailStatus, email) => {
      if (formContext.setEmailValidationStatus) {
        formContext.setEmailValidationStatus(id, emailStatus, email);
      }
    },
    [formContext, id]
  );

  // Custom setStatus that also updates form validation context
  const updateStatus = useCallback(
    (newStatus, message = '') => {
      setStatus(newStatus);
      setStatusMessage(message);
      updateEmailValidationStatus(newStatus, value);
    },
    [value, updateEmailValidationStatus]
  );

  const defaultMessages = {
    checking: t('email_check_widget.status.checking'),
    exists: t('email_check_widget.status.exists'),
    available: t('email_check_widget.status.available'),
    error: t('email_check_widget.status.error'),
    invalid: t('email_check_widget.status.invalid'),
    ...customMessages,
  };

  const checkEmail = useCallback(
    async email => {
      if (!email || !isValidEmail(email)) {
        updateStatus('idle');
        return;
      }

      updateStatus('checking', defaultMessages.checking);

      try {
        const response = await mdms.get(`${checkApiUrl}?email=${encodeURIComponent(email)}`);

        if (response.ok) {
          const result = await response.json();

          if (result && result.user_guid) {
            updateStatus('exists', showExistsMessage ? defaultMessages.exists : '');
          } else if (result && !result.user_guid && result.has_form_submission) {
            updateStatus('under_review', '');
          } else {
            updateStatus('available', showAvailableMessage ? defaultMessages.available : '');
          }
        } else {
          throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        updateStatus('error', defaultMessages.error);
      }
    },
    [checkApiUrl, showAvailableMessage, showExistsMessage, defaultMessages, updateStatus]
  );

  const handleChange = useCallback(
    event => {
      const newValue = event.target.value;
      onChange(newValue);

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      if (!newValue) {
        updateStatus('idle');
        return;
      }

      if (!isValidEmail(newValue)) {
        updateStatus('invalid', defaultMessages.invalid);
        return;
      }

      // Set new debounce timer
      const timer = setTimeout(() => {
        checkEmail(newValue);
      }, debounceMs);

      setDebounceTimer(timer);
    },
    [onChange, debounceTimer, debounceMs, checkEmail, defaultMessages, updateStatus]
  );

  useEffect(
    () => () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    },
    [debounceTimer]
  );

  useEffect(() => {
    if (!value) {
      updateStatus('idle');
    }
  }, [value, updateStatus]);

  const renderStatusIcon = () => {
    switch (status) {
      case 'checking':
        return <LoadingSpinner />;
      case 'exists':
        return <Icon type="times-circle" />;
      case 'under_review':
        return <Icon type="times-circle" />;
      case 'available':
        return <Icon type="check-circle" />;
      case 'error':
        return <Icon type="exclamation-triangle" />;
      case 'invalid':
        return <Icon type="times-circle" />;
      default:
        return null;
    }
  };

  const getMessageType = () => {
    switch (status) {
      case 'checking':
        return 'checking';
      case 'exists':
      case 'under_review':
      case 'error':
      case 'invalid':
        return 'error';
      case 'available':
        return 'success';
      default:
        return 'info';
    }
  };

  const hasError =
    rawErrors.length > 0 ||
    status === 'exists' ||
    status === 'under_review' ||
    status === 'error' ||
    status === 'invalid';

  return (
    <div>
      <EmailInputWrapper>
        <EmailInput
          id={id}
          type="email"
          value={value}
          placeholder={placeholder || t('email_check_widget.placeholder')}
          onChange={handleChange}
          disabled={disabled || readonly}
          hasError={hasError}
          status={status}
          aria-invalid={hasError}
          aria-describedby={statusMessage ? `${id}-status` : undefined}
        />
        {status !== 'idle' && <StatusIcon status={status}>{renderStatusIcon()}</StatusIcon>}
      </EmailInputWrapper>
      {statusMessage && (
        <StatusMessage
          id={`${id}-status`}
          type={getMessageType()}
          role={
            status === 'exists' || status === 'under_review' || status === 'error' || status === 'invalid'
              ? 'alert'
              : 'status'
          }
        >
          {statusMessage}
        </StatusMessage>
      )}
      {status === 'exists' && (
        <AlertBox>
          <div>
            <strong>{t('email_check_widget.alerts.exists.title')}</strong>{' '}
            {t('email_check_widget.alerts.exists.message')}{' '}
            <a href="https://owenscorning.com/connect" target="_blank" rel="noopener noreferrer">
              {t('email_check_widget.alerts.exists.login_link_text')}
            </a>
          </div>
        </AlertBox>
      )}
      {status === 'under_review' && (
        <AlertBox>
          <div style={{ marginTop: '8px' }}>
            <strong>{t('email_check_widget.alerts.under_review.title')}:</strong>{' '}
            {t('email_check_widget.alerts.under_review.message')}
          </div>
        </AlertBox>
      )}
    </div>
  );
};

EmailCheckWidget.defaultProps = {
  value: '',
  disabled: false,
  readonly: false,
  placeholder: null,
  options: {
    checkApiUrl: '/api/v1/contractor/users/contractor_rewards_lookup',
    debounceMs: 500,
    showAvailableMessage: true,
    showExistsMessage: true,
    customMessages: {},
  },
  rawErrors: [],
  formContext: {},
};

EmailCheckWidget.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  readonly: PropTypes.bool,
  placeholder: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.shape({
    checkApiUrl: PropTypes.string,
    debounceMs: PropTypes.number,
    showAvailableMessage: PropTypes.bool,
    showExistsMessage: PropTypes.bool,
    customMessages: PropTypes.shape({}),
  }),
  rawErrors: PropTypes.arrayOf(PropTypes.string),
  formContext: PropTypes.shape({}),
  t: PropTypes.func.isRequired,
};

export default connectLocation(EmailCheckWidget);
